import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { MapPin, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", city: "", country: "", lat: "", lng: "", radius_miles: 15, active: true, is_default: false };
const FIELDS = [["name", "Area name (e.g. Brooklyn)"], ["city", "City"], ["country", "Country"], ["lat", "Centre latitude"], ["lng", "Centre longitude"], ["radius_miles", "Radius (miles)"]];

function AreaForm({ onDone }) {
  const [f, setF] = useState(EMPTY);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/locations", { ...f, lat: Number(f.lat), lng: Number(f.lng), radius_miles: Number(f.radius_miles) });
      toast.success("Launch area added"); setF(EMPTY); onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <form onSubmit={save} data-testid="area-form" className="grid sm:grid-cols-3 gap-2">
      {FIELDS.map(([k, ph]) => (
        <input key={k} data-testid={`area-${k}`} required value={f[k]} placeholder={ph} type={["lat", "lng", "radius_miles"].includes(k) ? "number" : "text"} step="any"
          onChange={(e) => setF({ ...f, [k]: e.target.value })}
          className="h-10 rounded-xl bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-amber-400/40" />
      ))}
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="area-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Active</label>
      <button data-testid="area-add-btn" type="submit" className="sm:col-span-2 btn-gold rounded-full h-10 text-sm font-semibold">Add launch area</button>
    </form>
  );
}

export function LocationsPanel() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-locations"], queryFn: async () => (await api.get("/admin/locations")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-locations"] });
  const update = async (a, patch) => {
    const { id, users, created_at, ...rest } = a;
    try { await api.put(`/admin/locations/${id}`, { ...rest, ...patch }); refresh(); toast.success("Area updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div data-testid="locations-panel" className="space-y-5">
      <div className="glass rounded-2xl p-5">
        <h3 className="font-display font-bold flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-300" /> Launch areas</h3>
        <p className="text-xs text-slate-400 mt-1">People only see profiles and events inside their area's radius. New sign-ups join the default area. The Global Test Event ignores areas.</p>
        <div className="mt-4 divide-y divide-white/5">
          {data.map((a) => (
            <div key={a.id} data-testid={`area-row-${a.id}`} className="py-3 flex flex-wrap items-center gap-3 text-sm">
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold flex items-center gap-1.5">{a.name}, {a.city} {a.is_default && <span className="text-[10px] uppercase font-bold text-amber-300 border border-amber-300/50 rounded-full px-2">Default</span>}</p>
                <p className="text-xs text-slate-400">{a.country} · {a.lat.toFixed(4)}, {a.lng.toFixed(4)} · {a.users} members</p>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-slate-300">
                <input data-testid={`area-radius-${a.id}`} type="number" min="1" max="200" defaultValue={a.radius_miles}
                  onBlur={(e) => Number(e.target.value) !== a.radius_miles && update(a, { radius_miles: Number(e.target.value) })}
                  className="w-16 h-8 rounded-lg bg-white/5 border border-white/10 px-2 text-sm" /> mi
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-300">Active <Switch data-testid={`area-active-${a.id}`} checked={a.active} onCheckedChange={(v) => update(a, { active: v })} /></label>
              {!a.is_default && (
                <button data-testid={`area-default-${a.id}`} onClick={() => update(a, { is_default: true, active: true })} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Make default</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-2xl p-5">
        <h3 className="font-display font-bold mb-3">Add an area (e.g. your US launch city)</h3>
        <AreaForm onDone={refresh} />
      </div>
    </div>
  );
}
